
import { defineComponent, nextTick, reactive, ref } from "vue";
import store from "@/store/store";
import { PLAY_STYLE, PLAY_STYLE_STRING, PlayStyle } from "@/common/types/PlayStyle";
import { useRoute } from "vue-router";
import { routes } from "@/router";
import playStyleIcon from "@/components/svg/PlayStyleIcon.vue";
import Login from "@/components/Login.vue";
import { isRecord } from "@/utils/isRecord";
import { checkMaintenanceMode } from "@/utils/checkMaintenanceMode";
import { getRedirectResult } from "firebase/auth";
import { auth, authErrorHandler } from "@/plugins/firebase";
import ErrorAlert from "@/components/ErrorAlert.vue";
import { LATEST_VERSION, VERSION } from "@/common/types/Version";
import { useTheme } from "vuetify/framework";

export type MenuIcon = { icon: string; label?: string; fn: () => void };
export type MenuList = { label: string; fn: () => void };

export default defineComponent({
  name: "App",
  components: { ErrorAlert, Login },
  mounted() {
    store.dispatch("auth/startListener");
  },
  beforeUnmount() {
    store.dispatch("auth/endListener");
  },
  setup() {
    const app = reactive({
      title: "IIDX Viewer",
      menuIcon: [] as MenuIcon[],
      menuList: [] as MenuList[],
      displayKeepAlive: true,
      loginDialog: false,
      doneLogin: false,
      doneLoginMessage: null as string | null,
      doneLogout: false,
      swUpdated: false,
      error: null,
      handler: null as ((e: Error) => string | null) | null,
      maintenanceMode: false,
    });
    const drawer = reactive<{ drawer: boolean | null }>({ drawer: null });
    const list = ref(
      routes.value.filter((e) => e.meta?.label !== undefined && e.meta?.icon !== undefined && !e.meta?.requiresAuth)
    );

    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          const providerNames: Record<string, string> = {
            "google.com": "Google",
            "twitter.com": "Twitter",
          };

          const messages = {
            link: `${result.providerId ? providerNames[result.providerId] : ""}アカウントがリンクされました`,
            reauthenticate: "再認証されました",
            signIn: "ログインしました",
          };

          app.doneLoginMessage = messages[result.operationType];
          app.doneLogin = true;
        }
      })
      .catch((error) => {
        app.error = error;
        app.handler = authErrorHandler;
      });

    store.watch(
      (state) => state.auth.user,
      (user) => {
        list.value = routes.value
          .filter(
            (e) =>
              e.meta?.label !== undefined &&
              e.meta?.icon !== undefined &&
              (e.meta?.requiresAuth ? user : true) &&
              (e.meta?.requiresAdmin ? store.state.auth.claims?.admin ?? false : true)
          )
          .map((e) => {
            if (e.meta === undefined || e.meta.params === undefined || !isRecord(e.meta?.params)) {
              return e;
            }
            const result = { ...e };
            for (const param of Object.keys(e.meta?.params)) {
              result.path = result.path.replace(`:${param}`, e.meta.params[param] ?? "");
            }
            return result;
          });
      }
    );

    const vuetify = useTheme();

    store.watch(
      (state) => [state.pref, state.auth.user],
      () => {
        const theme = store.getters["pref/getPref"].theme;
        if (
          !Object.values(VERSION)
            .filter((e) => e >= VERSION.HEROIC_VERSE)
            .includes(theme.color)
        ) {
          theme.color = -1;
        }

        const color = theme.color === -1 ? LATEST_VERSION : theme.color;

        vuetify.themes.value.light.colors.primary = vuetify.themes.value.light.colors[`primary${color}`];
        vuetify.themes.value.light.colors["primary-darken-1"] =
          vuetify.themes.value.light.colors[`primary${color}-darken`];
        vuetify.themes.value.light.colors["primary-lighten-1"] =
          vuetify.themes.value.light.colors[`primary${color}-lighten`];
        vuetify.themes.value.light.colors["on-primary"] = vuetify.themes.value.light.colors[`on-primary${color}`];
        document
          .querySelector("meta[name='theme-color']")
          ?.setAttribute("content", vuetify.themes.value.light.colors["primary-darken-1"]);
      },
      { deep: true, immediate: true }
    );

    checkMaintenanceMode().then((value) => {
      app.maintenanceMode = value;
    });

    const playStyle = reactive<{ playStyle: PlayStyle }>({ playStyle: store.getters["pref/getPlayStyle"] });

    const togglePlayStyle = () => {
      playStyle.playStyle = playStyle.playStyle === PLAY_STYLE.SP ? PLAY_STYLE.DP : PLAY_STYLE.SP;
      store.commit("pref/setPlayStyle", playStyle.playStyle);
      cacheClear();
    };

    const barTitle = (title: string) => {
      app.title = title;
    };

    const barMenuIcon = (menuIcon: MenuIcon[]) => {
      app.menuIcon = menuIcon;
    };

    const barMenuList = (menuList: MenuList[]) => {
      app.menuList = menuList;
    };

    const cacheClear = () => {
      app.displayKeepAlive = false;
      nextTick(() => (app.displayKeepAlive = true));
    };

    const login = () => {
      app.loginDialog = true;
    };

    const route = useRoute();

    const getComponentKey = (component: { type: { name: string } } | undefined) => {
      if (!component) {
        return "";
      }

      return route.path;
    };

    const reload = () => {
      window.location.reload();
    };

    document.addEventListener(
      "swUpdated",
      (e) => {
        if (e instanceof CustomEvent<ServiceWorkerRegistration> && e.detail.waiting) {
          app.swUpdated = true;
          e.detail.waiting.postMessage({ type: "SKIP_WAITING" });
        }
      },
      { once: true }
    );

    return {
      app,
      drawer,
      list,
      playStyle,
      togglePlayStyle,
      barTitle,
      barMenuIcon,
      barMenuList,
      cacheClear,
      login,
      getComponentKey,
      reload,
      PLAY_STYLE,
      PLAY_STYLE_STRING,
      playStyleIcon,
    };
  },
});
